import { PlaceAutocomplete } from "@/components/others/place-autocomplete";
import { PlaceSuggestion } from "@/components/others/place-autocomplete/types";
import { trackHomeSearch } from "@/utils/analytics/track-search";
import { cn } from "@/utils/cn";

import { LocationPermissionDialog } from "@/components/location-permission-dialog";
import { Button } from "@/components/rac/button";
import { useLocationPermissionStore } from "@/stores/location-permission-store";
import { isLocationInBangalore } from "@/utils/location-utils";
import { Loader2, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { ConfirmationDialog } from "./confirmation-dialog";
import { baseInputClasses } from "./input";
import type { SearchSectionProps } from "./types";

interface SearchFormProps extends SearchSectionProps {
	onSearchWithFallback: (suggestion: PlaceSuggestion) => void;
	isLoading: boolean;
	onSearchWithCoords?: (coords: { lat: number; long: number }) => void;
}

const baseFormClasses = "flex flex-col md:flex-row gap-0 max-w-[746px] items-stretch md:items-start transform-gpu";

const baseButtonClasses = cn(
	"text-lg font-medium h-[60px] md:h-[68px] lg:h-[72px] px-6",
	"rounded-none rounded-b-xl md:rounded-l-none md:rounded-r-xl border-2 border-l-0 md:border-l-0",
	"transition-colors duration-1000 ease-in-out transform-gpu active:scale-100 flex items-center justify-center",
	"w-full md:w-[140px] lg:w-[160px]",
);

export const SearchLocationForm: React.FC<SearchFormProps> = ({
	className = "",
	onSearchWithFallback,
	isLoading,
	isLoggedIn = false,
	onSearchWithCoords,
}) => {
	const [suggestion, setSuggestion] = useState<PlaceSuggestion | null>(null);
	const [activeDialog, setActiveDialog] = useState<
		"none" | "locationPermission" | "outsideBangalore" | "generalSearch"
	>("none");

	const { getCurrentLocation, clearError, isGettingLocation, checkPermissionStatus } = useLocationPermissionStore();

	const [hasTransitioned, setHasTransitioned] = useState(false);
	const [displayAsLoggedIn, setDisplayAsLoggedIn] = useState(false);

	useEffect(() => {
		checkPermissionStatus();
	}, [checkPermissionStatus]);

	useEffect(() => {
		if (!hasTransitioned && isLoggedIn) {
			const timer = setTimeout(() => {
				setDisplayAsLoggedIn(true);
				setHasTransitioned(true);
			}, 100);

			return () => clearTimeout(timer);
		} else if (!isLoggedIn) {
			setDisplayAsLoggedIn(false);
			setHasTransitioned(false);
		}
	}, [isLoggedIn, hasTransitioned]);

	const handleSelectionMade = (suggestion: PlaceSuggestion) => {
		setSuggestion(suggestion);
	};

	const handleSearchSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		trackHomeSearch();

		if (suggestion) {
			onSearchWithFallback(suggestion);
			return;
		}

		// FIX 1: Check if permission is already granted
		const currentPermissionStatus = useLocationPermissionStore.getState().permissionStatus;

		if (currentPermissionStatus === "granted") {
			// Permission already granted, get location directly
			try {
				const position = await getCurrentLocation({ enableHighAccuracy: true, timeout: 30000 });
				handleLocationReady(position.coords);
			} catch (_error) {
				// Location retrieval failed, show general search
				setActiveDialog("generalSearch");
			}
		} else {
			// Permission not granted, show permission dialog
			setActiveDialog("locationPermission");
		}
	};

	const handleLocationReady = (location: { latitude: number; longitude: number }) => {
		// FIX 2: Fix the double negative logic - simplified
		if (!isLocationInBangalore(location)) {
			setActiveDialog("outsideBangalore");
			return;
		}
		const coords = { lat: location.latitude, long: location.longitude };
		onSearchWithCoords?.(coords);
		setActiveDialog("none");
	};

	const handleLocationPermissionDenied = () => {
		setActiveDialog("generalSearch");
	};

	const handleUseBangaloreCenter = () => {
		const bangaloreCenter = { latitude: 12.9716, longitude: 77.5946 };
		// FIX 3: Use coordinates directly instead of calling handleLocationReady
		// which would check Bangalore bounds again
		const coords = { lat: bangaloreCenter.latitude, long: bangaloreCenter.longitude };
		onSearchWithCoords?.(coords);
		setActiveDialog("none");
	};

	const handleStayOnCurrentScreen = () => {
		setActiveDialog("none");
	};

	const closeAllDialogs = () => {
		setActiveDialog("none");
		clearError();
	};

	const isFormLoading = isLoading || isGettingLocation;

	return (
		<>
			<LocationPermissionDialog
				open={activeDialog === "locationPermission"}
				onOpenChange={open => !open && closeAllDialogs()}
				onPermissionGranted={position => {
					const coords = {
						latitude: position.coords.latitude,
						longitude: position.coords.longitude,
					};
					handleLocationReady(coords);
				}}
				onPermissionDenied={handleLocationPermissionDenied}
				onCancel={closeAllDialogs}
			/>

			<ConfirmationDialog
				open={["outsideBangalore", "generalSearch"].includes(activeDialog)}
				onOpenChange={open => !open && closeAllDialogs()}
				onConfirm={handleUseBangaloreCenter}
				onCancel={handleStayOnCurrentScreen}
				isLoading={false}
			/>

			<form onSubmit={handleSearchSubmit}>
				<div id="search-form" className={cn(baseFormClasses, className)}>
					<div className="flex-1">
						<PlaceAutocomplete
							onSelectionMade={handleSelectionMade}
							placeholder="Search Locality / Project name"
							className={cn(
								baseInputClasses,
								displayAsLoggedIn
									? "border-gray-300 focus-within:border-gray-400"
									: "border-primary-700 focus-within:border-primary-700",
							)}
							inputProps={{
								"aria-label": "Search property by Locality or Project name",
								className:
									"border-0 outline-none ring-0 focus:border-0 focus:outline-none focus:ring-0 rounded-none unfocusable",
								style: {
									boxShadow: "none",
									border: "none",
									outline: "none",
								},
							}}
							restrictions={{
								includedPrimaryTypes: ["locality", "sublocality", "establishment"],
								language: "en",
								region: "IN",
								radius: 30,
							}}
						/>
					</div>
					<Button
						type="submit"
						aria-label="Search properties"
						variant="bareshell"
						isDisabled={isFormLoading}
						className={cn(
							baseButtonClasses,
							displayAsLoggedIn
								? "bg-gray-200 text-gray-600 border-gray-300"
								: "bg-primary-700 text-primary-foreground border-primary-700",
							isFormLoading && "data-[disabled]:opacity-100",
						)}
					>
						{isFormLoading ? (
							<>
								<Loader2
									className={`${isLoggedIn ? "w-4 h-4" : "w-5 h-5 md:w-6 md:h-6"} mr-2 animate-spin`}
									aria-hidden="true"
								/>
								<span className="md:hidden lg:block">{isGettingLocation ? "Getting Location..." : "Loading..."}</span>
							</>
						) : (
							<>
								<Search className={`${isLoggedIn ? "w-4 h-4" : "w-5 h-5 md:w-6 md:h-6"} mr-2`} aria-hidden="true" />
								<span className="md:hidden lg:block">Search</span>
							</>
						)}
					</Button>
				</div>
			</form>
		</>
	);
};
